import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Snackbar, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import PATHS from '../../lib/consts/paths';
import { LegalDocs } from '../../lib/consts';
import { ILegals } from '../../lib/interfaces/Legals';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import { updateCookies } from '../../api/apiRequests/cookies';
import useGetMetadataQuery from '../../reactQuery/hooks/useGetMetadata';
import ContentRenderer from '../Smart/Content/ContentRenderer';
import { contentMandatoryFields } from '../Form/form.utils';
import './CookiesBar.scss';

type CookiesBarProps = {
	version: number;
};

const Cookies: FC<CookiesBarProps> = ({ version }) => {
	const { t, i18n } = useTranslation();
	const [showCookiesBar, setShowCookiesBar] = useState<boolean>(false);
	const { data: currentUser } = useUserMeQuery();
	const { data: metadata } = useGetMetadataQuery();
	const cookiesMetadata = metadata?.legals?.find(
		(legal: ILegals) => legal?.identity === LegalDocs.COOKIES_MESSAGE,
	);
	const cookiesMetadataVersion = cookiesMetadata?.version;
	const currentLanguage: string = i18n.language;
	const contentFields = cookiesMetadata?.content[currentLanguage];

	const cookiesUserVersion = currentUser?.legal?.find(
		(legal: { legalDocType: LegalDocs }) =>
			legal.legalDocType === LegalDocs.COOKIES_MESSAGE,
	)?.version;

	const updateCookiesMutation = useMutation<any, AxiosError<any>, string>(
		() => updateCookies(),
		{
			onSuccess() {
				setShowCookiesBar(false);
			},
			onError: () => {},
		},
	);

	const handleAcceptCookies = () => {
		if (currentUser && cookiesMetadataVersion) {
			updateCookiesMutation.mutate('');
			localStorage.setItem('isCookiesAccepted', cookiesMetadataVersion);
		} else if (cookiesMetadataVersion) {
			localStorage.setItem('isCookiesAccepted', cookiesMetadataVersion);
			setShowCookiesBar(false);
		}
	};

	const cookiesLocalStorageVersion = localStorage.getItem('isCookiesAccepted');
	const userLocalStorage = localStorage.getItem('currentUser');

	useEffect(() => {
		const condition =
			!userLocalStorage && cookiesLocalStorageVersion !== cookiesMetadataVersion;
		setShowCookiesBar(condition);
	}, [currentUser]);

	useEffect(() => {
		if (metadata) {
			if (currentUser) {
				if (
					cookiesUserVersion !== cookiesMetadataVersion &&
					cookiesLocalStorageVersion !== cookiesMetadataVersion
				) {
					setShowCookiesBar(true);
				} else if (
					cookiesLocalStorageVersion === cookiesMetadataVersion &&
					cookiesUserVersion !== cookiesMetadataVersion
				) {
					updateCookiesMutation.mutate('');
				} else if (
					cookiesMetadataVersion &&
					cookiesUserVersion === cookiesMetadataVersion &&
					cookiesLocalStorageVersion !== cookiesMetadataVersion
				) {
					localStorage.setItem('isCookiesAccepted', cookiesMetadataVersion);
				}
			} else if (cookiesLocalStorageVersion !== cookiesMetadataVersion) {
				setShowCookiesBar(true);
			}
		}
	}, [currentUser, metadata]);

	const cookiesContent = contentFields?.map((field) => {
		if (field.name === `${contentMandatoryFields.CONTENT}_${currentLanguage}`) {
			return (
				<>
					<Typography className="CookiesBar__main-text-content">
						{contentFields?.map((field) => {
							if (
								field.name === `${contentMandatoryFields.CONTENT}_${currentLanguage}`
							) {
								return (
									<ContentRenderer
										textColor="#ffffff"
										html={field.content}
										key={field.name}
									/>
								);
							}
						})}
					</Typography>
					<Link
						target="_blank"
						to={PATHS.cookiesInfo}
						className="CookiesBar__more-link"
					>
						{t('moreInformation')}
					</Link>
				</>
			);
		}
	});

	return version === 1 ? (
		<div>
			{cookiesMetadata && showCookiesBar && (
				<div className="CookiesBar">
					<div className="CookiesBar__wrapper">
						<div className="CookiesBar__main-text">{cookiesContent}</div>
						<div className="CookiesBar__buttons-wrapper">
							<Button
								sx={{
									margin: '0 5px',
									backgroundColor: '#fff',
									color: '#302F31',
									':hover': {
										bgcolor: '#F1F1F2',
										color: '#302F31',
									},
								}}
								variant="contained"
								onClick={handleAcceptCookies}
								className="CookiesBar__button"
								size="small"
								disableElevation
							>
								{t('accept')}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	) : (
		<Snackbar
			open={cookiesMetadata && showCookiesBar}
			// autoHideDuration={5000}
			onClose={handleAcceptCookies}
			sx={{ maxWidth: '800px' }}
			message={cookiesContent}
			action={
				<div style={{ margin: '5px 10px 15px 5px' }}>
					<Button
						color="secondary"
						variant="contained"
						size="medium"
						onClick={handleAcceptCookies}
					>
						{t('accept')}
					</Button>
				</div>
			}
		/>
	);
};

export default Cookies;
